import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LangProvider } from "@/lib/lang";
import PublicLayout from "@/components/layout/PublicLayout";
import Home from "@/pages/Home";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Areas from "@/pages/Areas";
import AreaDetail from "@/pages/AreaDetail";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Agents from "@/pages/Agents";
import AgentDetail from "@/pages/AgentDetail";
import Journal from "@/pages/Journal";
import JournalDetail from "@/pages/JournalDetail";
import Calculators from "@/pages/Calculators";
import MortgagePrequal from "@/pages/MortgagePrequal";
import Favorites from "@/pages/Favorites";
import Compare from "@/pages/Compare";
import Contact from "@/pages/Contact";
import Valuation from "@/pages/Valuation";
import ListWithUs from "@/pages/ListWithUs";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Developers from "@/pages/Developers";
import DeveloperDetail from "@/pages/DeveloperDetail";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Faq from "@/pages/Faq";
import GoldenVisaPillar from "@/pages/pillars/GoldenVisaPillar";
import DLDFeesPillar from "@/pages/pillars/DLDFeesPillar";
import OffPlanPaymentPlansPillar from "@/pages/pillars/OffPlanPaymentPlansPillar";
import BestAreasPillar from "@/pages/pillars/BestAreasPillar";
import DubaiMortgagePillar from "@/pages/pillars/DubaiMortgagePillar";
import BuyFromAbroadPillar from "@/pages/pillars/BuyFromAbroadPillar";
import PropertyTaxPillar from "@/pages/pillars/PropertyTaxPillar";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminListings from "@/pages/admin/Listings";
import AdminListingForm from "@/pages/admin/ListingForm";
import AdminLeads from "@/pages/admin/Leads";
import AdminAreas from "@/pages/admin/AreaGuides";
import AdminAgents from "@/pages/admin/Agents";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminProjects from "@/pages/admin/Projects";
import AdminProjectForm from "@/pages/admin/ProjectForm";
import AdminCommunities from "@/pages/admin/Communities";
import AdminCommunityForm from "@/pages/admin/CommunityForm";
import AdminImports from "@/pages/admin/Imports";
import AdminApprovals from "@/pages/admin/Approvals";
import AdminEmailTemplates from "@/pages/admin/EmailTemplates";
import AdminLeadDetail from "@/pages/admin/LeadDetail";
import AdminTranslations from "@/pages/admin/Translations";
import AdminRentals from "@/pages/admin/Rentals";
import AdminDevelopers from "@/pages/admin/Developers";
import AdminDeveloperForm from "@/pages/admin/DeveloperForm";
import AdminJournal from "@/pages/admin/Journal";
import AdminProfile from "@/pages/admin/Profile";
import InteractiveMap from "@/pages/InteractiveMap";

function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen bg-white flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <LangProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster theme="dark" position="top-right" />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/properties" element={<Properties key="buy" mode="buy" />} />
            <Route path="/rent" element={<Properties key="rent" mode="rent" />} />
            <Route path="/off-plan" element={<Properties key="off-plan" mode="off-plan" />} />
            <Route path="/commercial" element={<Properties key="commercial" mode="commercial" />} />
            <Route path="/properties/:id" element={<PropertyDetail />} />
            <Route path="/areas" element={<Areas />} />
            <Route path="/areas/:slug" element={<AreaDetail />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/agents/:id" element={<AgentDetail />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/journal/:slug" element={<JournalDetail />} />
            <Route path="/calculators" element={<Calculators />} />
            <Route path="/mortgage-pre-qualification" element={<MortgagePrequal />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/valuation" element={<Valuation />} />
            <Route path="/list-with-us" element={<ListWithUs />} />
            <Route path="/developers" element={<Developers />} />
            <Route path="/developers/:slug" element={<DeveloperDetail />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/map" element={<InteractiveMap />} />
            {/* Pillar / editorial guides — high-intent AEO / GEO landing pages */}
            <Route path="/guides/golden-visa-dubai" element={<GoldenVisaPillar />} />
            <Route path="/guides/dubai-property-closing-costs" element={<DLDFeesPillar />} />
            <Route path="/guides/dubai-off-plan-payment-plans" element={<OffPlanPaymentPlansPillar />} />
            <Route path="/guides/best-areas-to-invest-in-dubai-2026" element={<BestAreasPillar />} />
            <Route path="/guides/dubai-mortgage-for-expats-2026" element={<DubaiMortgagePillar />} />
            <Route path="/guides/buy-dubai-property-from-abroad" element={<BuyFromAbroadPillar />} />
            <Route path="/guides/dubai-property-tax-guide-2026" element={<PropertyTaxPillar />} />
          </Route>

          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/admin"
            element={<RequireAuth><AdminLayout /></RequireAuth>}
          >
            <Route index element={<AdminDashboard />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="listings" element={<AdminListings />} />
            <Route path="listings/new" element={<AdminListingForm />} />
            <Route path="listings/:id/edit" element={<AdminListingForm />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="areas" element={<AdminAreas />} />
            <Route path="agents" element={<AdminAgents />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="projects/new" element={<AdminProjectForm />} />
            <Route path="projects/:slug/edit" element={<AdminProjectForm />} />
            <Route path="communities" element={<AdminCommunities />} />
            <Route path="communities/new" element={<AdminCommunityForm />} />
            <Route path="communities/:slug/edit" element={<AdminCommunityForm />} />
            <Route path="imports" element={<AdminImports />} />
            <Route path="approvals" element={<AdminApprovals />} />
            <Route path="email-templates" element={<AdminEmailTemplates />} />
            <Route path="translations" element={<AdminTranslations />} />
            <Route path="rentals" element={<AdminRentals />} />
            <Route path="developers" element={<AdminDevelopers />} />
            <Route path="developers/new" element={<AdminDeveloperForm />} />
            <Route path="developers/:slug/edit" element={<AdminDeveloperForm />} />
            <Route path="journal" element={<AdminJournal />} />
            <Route path="leads/:id" element={<AdminLeadDetail />} />
            <Route path="profile" element={<AdminProfile />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
